import ReviewSort from '@/enums/ReviewSort';
import WidgetType from '@/enums/WidgetType';
import ReviewTag from '@/enums/ReviewTag';
import ReviewVendor from '@/enums/ReviewVendor';
import ReviewSource from '@/enums/ReviewSource';
import SocialMediaType from '@/enums/SocialMediaType';
import ExternalPlatformType from '@/enums/ExternalPlatformType';
import i18n from '@/lib/i18n';
import ordinalSuffix from '@/lib/ordinalSuffix';

const state = {
  rawReviews: [],
  pagy: {},
  totalReviewsCount: null,
  totalProductMediaReviewsCount: null,
  productMetaReviewsCount: null,
  productId: null,
  reviewSort: ReviewSort.RECENTLY_CREATED,
  isReviewWritable: false,
  isReviewWriteWhitelisted: false,
  mediaIndex: null,
  mediaReviewsFirst: 'false',
  hasMedia: false,
  editOrDeleteCallback: null,
  isDeletingReview: false
};

const getters = {
  reviews({ rawReviews }, getters) {
    return rawReviews.map(r => getters.displayableReview(r));
  },
  displayableReview: ({ reviewSort }, _getters, rootState) => rawReview => {
    const isDisplayableTag = (tagType, widgetType, reviewSort) => {
      switch (tagType) {
        case 'purchase_for_gift':
          return widgetType === WidgetType.PRODUCT_REVIEWS;
        case 'purchase_for_experience':
          return widgetType === WidgetType.PRODUCT_REVIEWS;
        case 'my_last_review':
          return widgetType === WidgetType.PRODUCT_REVIEWS;
        case 'show_pinned_to_top':
          return (
            widgetType === WidgetType.PRODUCT_REVIEWS &&
            [ReviewSort.MOST_LIKES, ReviewSort.AI_RECOMMENDATION].includes(
              reviewSort
            )
          );
        default:
          return true;
      }
    };

    return {
      ...rawReview,
      user_display_name: rawReview.review_vendor
        ? [
            ReviewVendor.REVU_SELECT,
            ReviewVendor.REVU,
            ReviewVendor.TAG_BY
          ].includes(rawReview.review_vendor)
          ? ReviewVendor.t(rawReview.review_vendor) // TODO: 외부출처 리뷰의 작성자명 설정 추가 후 변경 필요
          : i18n.t('review.user_display_name.third_party_author', [
              ReviewVendor.t(rawReview.review_vendor)
            ])
        : rawReview.user_display_name,
      tagLabels: rawReview.tags
        .filter(tag =>
          isDisplayableTag(
            tag.type,
            rootState.widget.widget.widget_type,
            reviewSort
          )
        )
        .map(tag => {
          switch (tag.type) {
            case ReviewTag.REVIEW_VENDOR:
              return ReviewVendor.t(rawReview.review_vendor);
            case ReviewTag.EXTERNAL_PLATFORM_TYPE:
              return i18n.t('ReviewTag.EXTERNAL_PLATFORM_TYPE', [
                ExternalPlatformType.t(rawReview.external_platform_type)
              ]);
            case ReviewTag.STORE_NAME:
              return tag.value;
            case ReviewTag.NTH_STEP_REVIEW:
              return i18n.t('ReviewTag.NTH_STEP_REVIEW', [
                tag.value,
                ordinalSuffix(tag.value)
              ]);
            default:
              return ReviewTag.t(tag.type);
          }
        })
    };
  },
  displayNewReviewButton(
    { isReviewWritable },
    _getters,
    { reviewSettings, widget },
    rootGetters
  ) {
    if (!reviewSettings.reviewSettings.review_form_enabled) {
      return (
        (isReviewWritable ||
          reviewSettings.reviewSettings.display_write_form_not_allowed_user) &&
        widget.widget.use_review_write_button
      );
    }

    if (!widget.widget.use_review_write_button) return false;

    return (
      !reviewSettings.reviewSettings.show_write_form_for_members_only ||
      rootGetters['session/isLoggedIn']
    );
  },
  isManagerReview: () => review => {
    return review.review_source === ReviewSource.MANAGER;
  },
  isEmbeddedSocialMediaType: () => review => {
    return [
      SocialMediaType.FACEBOOK,
      SocialMediaType.INSTAGRAM,
      SocialMediaType.YOUTUBE,
      SocialMediaType.VIMEO
    ].includes(review.social_media_type);
  },
  showScore: (_state, _getters, { reviewSettings }) => review => {
    return reviewSettings.reviewSettings.show_score && review.score > 0;
  },
  showDate: (_state, _getters, { reviewSettings }) => review => {
    return reviewSettings.reviewSettings.show_created_at && !!review.created_at;
  },
  showEditOrDelete: () => review => {
    return review.editable || review.deletable;
  },
  showScoreSection: (_state, getters) => review => {
    return (
      getters.showScore(review) ||
      getters.showDate(review) ||
      getters.showEditOrDelete(review)
    );
  },
  getCurrentPage: state => reviewId => {
    const minPage = state.pagy.prev ? state.pagy.prev + 1 : 1;
    const reviewIndex = state.rawReviews.findIndex(r => r.id === reviewId);
    const pageIndex = Math.floor(reviewIndex / state.pagy.items);
    const currentPage = minPage + pageIndex;
    return currentPage;
  }
};

const mutations = {
  SET_REVIEWS(state, data) {
    state.rawReviews = data.reviews;
    if (data.pagy) state.pagy = data.pagy;
  },
  APPEND_REVIEWS(state, data) {
    state.rawReviews = [...state.rawReviews, ...data.reviews];
    const prev = state.pagy.prev;
    state.pagy = data.pagy;
    state.pagy.prev = prev;
  },
  PREPEND_REVIEWS(state, data) {
    state.rawReviews = [...data.reviews, ...state.rawReviews];
    const next = state.pagy.next;
    state.pagy = data.pagy;
    state.pagy.next = next;
  },
  SET_TOTAL_REVIEWS_COUNT(state, totalReviewsCount) {
    state.totalReviewsCount = totalReviewsCount;
  },
  SET_PRODUCT_META_REVIEWS_COUNT(state, productMetaReviewsCount) {
    state.productMetaReviewsCount = productMetaReviewsCount;
  },
  SET_PRODUCT_ID(state, productId) {
    state.productId = productId;
  },
  SET_TOTAL_PRODUCT_MEDIA_REVIEWS_COUNT(state, totalProductMediaReviewsCount) {
    state.totalProductMediaReviewsCount = totalProductMediaReviewsCount;
  },
  SET_REVIEW_SORT(state, reviewSort) {
    state.reviewSort = reviewSort;
  },
  SET_REVIEW_WRITABLE(state, isReviewWritable) {
    state.isReviewWritable = isReviewWritable;
  },
  SET_REVIEW_WRITE_WHITELISTED(state, isReviewWriteWhitelisted) {
    state.isReviewWriteWhitelisted = isReviewWriteWhitelisted;
  },
  SET_MEDIA_INDEX(state, mediaIndex) {
    state.mediaIndex = mediaIndex;
  },
  SET_MEDIA_REVIEWS_FIRST(state, media_reviews_first) {
    state.mediaReviewsFirst = media_reviews_first;
  },
  SET_HAS_MEDIA(state, hasMedia) {
    state.hasMedia = hasMedia;
  },
  SET_EDIT_OR_DELETE_CALLBACK(state, refreshFn) {
    state.editOrDeleteCallback = refreshFn;
  },
  SET_IS_DELETING_REVIEW(state, isDeleting) {
    state.isDeletingReview = isDeleting;
  }
};

const actions = {
  fetchReviews(
    { commit, rootState },
    { params, append = false, prepend = false }
  ) {
    return rootState.api
      .fetchReviews({
        ...params,
        widget_id: rootState.widget.widget.id,
        product_code: rootState.productCode
      })
      .then(({ data }) => {
        commit(
          append
            ? 'APPEND_REVIEWS'
            : prepend
            ? 'PREPEND_REVIEWS'
            : 'SET_REVIEWS',
          data
        );
        commit('SET_REVIEW_SORT', data.review_sort);
        if ('total_reviews_count' in data)
          commit('SET_TOTAL_REVIEWS_COUNT', data.total_reviews_count);
        if ('total_product_media_reviews_count' in data)
          commit(
            'SET_TOTAL_PRODUCT_MEDIA_REVIEWS_COUNT',
            data.total_product_media_reviews_count
          );
        if ('product_meta_reviews_count' in data)
          commit(
            'SET_PRODUCT_META_REVIEWS_COUNT',
            data.product_meta_reviews_count
          );
        if ('product_id' in data) commit('SET_PRODUCT_ID', data.product_id);
        if ('has_media' in data) commit('SET_HAS_MEDIA', data.has_media);
        commit('widget/SET_HAS_MINIMUM_ITEMS', data.has_minimum_reviews, {
          root: true
        });
      });
  },
  fetchReviewWritableCheck({ commit, rootState }) {
    return rootState.api
      .fetchReviewWritableCheck(rootState.productCode)
      .then(({ data }) => {
        commit('SET_REVIEW_WRITABLE', data.writable);
        commit('SET_REVIEW_WRITE_WHITELISTED', data.whitelisted);
      });
  },
  fetchManagingReviewsWrittenReviews(
    { commit, rootState },
    { params, append = false, prepend = false }
  ) {
    return rootState.api
      .fetchManagingReviewsWrittenReviews(params)
      .then(({ data }) => {
        commit(
          append
            ? 'APPEND_REVIEWS'
            : prepend
            ? 'PREPEND_REVIEWS'
            : 'SET_REVIEWS',
          data
        );
      });
  },
  deleteReviewWithConfirmation({ commit, dispatch, rootState }, review) {
    if (review.brand_user_id) {
      return rootState.window.windowService
        .confirm(i18n.t('review.delete_confirmation'))
        .then(response => {
          if (response) {
            commit('SET_IS_DELETING_REVIEW', true);
            return rootState.api
              .deleteReview(review.id)
              .finally(() => commit('SET_IS_DELETING_REVIEW', false));
          } else {
            return Promise.reject();
          }
        });
    } else {
      return dispatch(
        'dialog/openReviewPasswordDialog',
        {
          id: review.id,
          mode: 'delete'
        },
        { root: true }
      ).then(password => {
        if (password) {
          commit('SET_IS_DELETING_REVIEW', true);
          return rootState.api
            .deleteReview(review.id, password)
            .finally(() => commit('SET_IS_DELETING_REVIEW', false));
        } else {
          return Promise.reject();
        }
      });
    }
  },
  editReview({ dispatch }, { review }) {
    if (review.brand_user_id) {
      return dispatch(
        'dialog/openReviewEditDialog',
        { id: review.id },
        { root: true }
      );
    } else {
      return dispatch(
        'dialog/openReviewPasswordDialog',
        { id: review.id, mode: 'edit' },
        { root: true }
      ).then(password => {
        if (password) {
          return dispatch(
            'dialog/openReviewEditDialog',
            { id: review.id, password },
            { root: true }
          );
        }
      });
    }
  },
  handleEditOrDeleteReview({ dispatch, state }, { action, review }) {
    switch (action) {
      case 'delete':
        return dispatch('deleteReviewWithConfirmation', review).then(() => {
          if (state.editOrDeleteCallback) {
            state.editOrDeleteCallback();
          }
        });
      case 'edit':
        return dispatch('editReview', { review }).then(response => {
          if (response === 'saved' && state.editOrDeleteCallback) {
            state.editOrDeleteCallback();
          }
          return response;
        });
    }
  }
};

export default { namespaced: true, state, getters, mutations, actions };
